import { Fragment, type ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { decodeHTML, Kind, ListStyle, ListType, type Block, type RichText, type Span } from '@pkgs/api';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import styles from './RichText.module.css';

const renderSpans = (spans: Span[], keyPrefix: string): ReactNode[] => {
  return spans.flatMap((span, index) => {
    if (!span.text) return [];

    const key = `${keyPrefix}-span-${index}`;

    let content: ReactNode = decodeHTML(span.text);

    if (span.bold) content = <strong>{content}</strong>;
    if (span.italic) content = <em>{content}</em>;

    return <Fragment key={key}>{content}</Fragment>;
  });
};

const isListBlock = (block: Block): boolean => block.kind === Kind.LIST || Boolean(block.items?.length);

const renderParagraph = (block: Block, key: string): ReactNode | null => {
  if (!block.spans?.length) return null;

  const content = renderSpans(block.spans, key);
  if (content.length === 0) return null;

  return <Copy key={key} text={content} className={styles.paragraph} />;
};

const renderNestedBlocks = (blocks: Block[], keyPrefix: string, parentListType?: ListType): ReactNode[] => {
  if (blocks.length === 0) return [];

  const nestedList = renderList(
    {
      kind: Kind.LIST,
      listType: parentListType ?? ListType.UNORDERED,
      items: blocks,
    },
    `${keyPrefix}-nested-list`,
  );

  return nestedList ? [nestedList] : [];
};

const renderListItem = (item: Block, key: string): ReactNode | null => {
  const spanContent = renderSpans(item.spans ?? [], key);
  const nestedContent = renderNestedBlocks(item.items ?? [], key, item.listType ?? ListType.UNORDERED);
  const content = [...spanContent, ...nestedContent];

  if (content.length === 0) return null;

  return <Copy as="li" text={content} key={key} className={styles.listItem} />;
};

const listVariants = cva(styles.list, {
  variants: {
    listStyle: {
      [ListStyle.DIAMOND]: styles.diamondList,
      [ListStyle.BULLET]: styles.bulletList,
    },
    listType: {
      [ListType.ORDERED]: styles.orderedList,
      [ListType.UNORDERED]: undefined,
    },
  },
});

function renderList(block: Block, key: string): ReactNode | null {
  const listItems =
    block.items?.flatMap((item, index) => {
      const listItem = renderListItem(item, `${key}-item-${index}`);
      return listItem ? [listItem] : [];
    }) ?? [];

  const content =
    listItems.length > 0
      ? listItems
      : (() => {
          const fallbackItem = renderListItem(block, `${key}-item-0`);
          return fallbackItem ? [fallbackItem] : [];
        })();

  if (content.length === 0) return null;

  if (block.listType === ListType.ORDERED) {
    return (
      <ol key={key} className={listVariants({ listStyle: block.listStyle, listType: block.listType })}>
        {content}
      </ol>
    );
  }

  return (
    <ul key={key} className={listVariants({ listStyle: block.listStyle, listType: block.listType })}>
      {content}
    </ul>
  );
}

export const renderRichTextContent = (richText: RichText): ReactNode[] | null => {
  if (!richText.blocks) return null;

  const content = richText.blocks.flatMap((block, index) => {
    const key = `block-${index}`;
    const renderedBlock = isListBlock(block) ? renderList(block, key) : renderParagraph(block, key);

    return renderedBlock ? [renderedBlock] : [];
  });

  return content.length > 0 ? content : null;
};

export const getPreferredProfileContent = ({
  richText,
  text,
  formatPlainText = (value: string) => value,
}: {
  richText?: RichText;
  text?: string;
  formatPlainText?: (text: string) => ReactNode;
}): ReactNode | null => {
  if (richText) {
    const content = renderRichTextContent(richText);
    if (content) return content;
  }

  if (text) return formatPlainText(text);

  return null;
};
